/**
 * Asset runtime — resolves asset-id references into {@link ResolvedAsset}s before a
 * component renders. It is the single choke point for all image URLs (the future CDN /
 * optimization seam). Deterministic, no I/O. See ASSET_RUNTIME.md.
 */
import type { AssetLibrary } from '../../schema'
import type { ResolvedAsset } from '../types'
import { placeholderAsset } from './placeholder'

/** The asset resolver contract. */
export interface AssetResolver {
  /** Resolve one id; a missing id yields a placeholder (never throws). */
  resolve(id: string): ResolvedAsset
  /** Deep-resolve any `asset-*` id references within a props bag into ResolvedAssets. */
  resolveProps<T>(props: T): T
}

/** Build an AssetResolver from a (validated, engine-owned) asset library. */
export function createAssetResolver(library: AssetLibrary): AssetResolver {
  const byId = new Map<string, ResolvedAsset>()
  for (const a of library.items) {
    byId.set(a.id, { id: a.id, type: a.type, url: a.url, alt: a.alt, isPlaceholder: false })
  }

  const resolve = (id: string): ResolvedAsset => byId.get(id) ?? placeholderAsset(id)

  const deep = (value: unknown): unknown => {
    if (typeof value === 'string') {
      // Only `asset-*` strings are treated as references; all other content passes through.
      return value.startsWith('asset-') ? resolve(value) : value
    }
    if (Array.isArray(value)) {
      return value.map(deep)
    }
    if (value !== null && typeof value === 'object') {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = deep(v)
      }
      return out
    }
    return value
  }

  return {
    resolve,
    resolveProps<T>(props: T): T {
      return deep(props) as T
    },
  }
}
